/// <amd-module name="VSS/Platform/Util/Number" />

import { ICultureInfo, getInvariantCulture } from "../Culture";

/**
 * Parses a float using the separator defined by the locale. The built in parseFloat
 * only allows for '.' as the separator. If in a locale where ',' is used for the
 * separator it will substitute it with '.' so parseFloat works.
 *
 * NOTES:
 * - Only preprended +/- signs are supported.
 * - Doesn't check grouping format (e.g. 1,,00,0.1 => 1000.1).
 *
 * @deprecated Use parseFloatWithLocale instead
 * @param value Float string to parse
 * @param locale Locale to parse in. navigator.language is default
 */
export function parseFloatLocale(value: string, locale: string = navigator.language): number {
    const localeDecimalSeparator = Intl.NumberFormat(locale).format(1.1)[1];
    const localeGroupSeparator = Intl.NumberFormat(locale).format(1000)[1]; // returns 0 for Spanish culture. So that's why this method is deprecated

    return parseLocalizedNumber(value, localeDecimalSeparator, localeGroupSeparator);
}

/**
* Parses a number using the given locale separators. The built-in parseFloat
* only allows for '.' as the decimal separator and ',' as the group separator.
* This function can handle different separators based on the locale or culture settings.
*
* @param value Number string to parse
* @param localeDecimalSeparator The decimal separator used in the locale or culture
* @param localeGroupSeparator The group separator used in the locale or culture
* @returns The parsed number or NaN if the value is invalid
*/
function parseLocalizedNumber(value: string, localeDecimalSeparator: string | undefined, localeGroupSeparator: string | undefined): number {
    value = value.trim();

    const defaultCulture = getInvariantCulture();
    const decimalSeparatorToUse = localeDecimalSeparator ?? defaultCulture.numberFormat.NumberDecimalSeparator;
    const cultureSpecificGroupSeparator = localeGroupSeparator ?? defaultCulture.numberFormat.NumberGroupSeparator;
    const groupSeparatorToUse = [" ", "\u00A0", "\u2009", "\u202F"].includes(cultureSpecificGroupSeparator) ? "\s" : cultureSpecificGroupSeparator;

    let integer: string;
    let fraction: string | null;
    const decimalPos = value.indexOf(decimalSeparatorToUse);

    if (decimalPos < 0) {
        integer = value;
        fraction = null;
    } else {
        integer = value.substr(0, decimalPos);
        fraction = value.substr(decimalPos + 1);
    }
    
    if (groupSeparatorToUse) {
        const thousandSeparatorRegexp: RegExp = new RegExp("\\" +groupSeparatorToUse, "g");
        integer = integer.split(thousandSeparatorRegexp).join("");
    }

    const formatted = integer + (fraction ? "." + fraction : "");

    if (formatted.match(/^[+-]?\d*\.?\d*$/)) {
        return parseFloat(formatted);
    }

    return Number.NaN;
}

/**
* Parses a number using the given locale separators. The built-in parseFloat
* only allows for '.' as the decimal separator and ',' as the group separator.
* This function can handle different separators based on the locale or culture settings.
*
* @param value Number string to parse
* @param culture The culture to use for parsing
* @returns The parsed number or NaN if the value is invalid
*/
export function parseFloatWithLocale(value: string, culture?: ICultureInfo): number {
    const cultureToUse = culture ?? getInvariantCulture();

    const localeDecimalSeparator = cultureToUse.numberFormat.NumberDecimalSeparator
    const localeGroupSeparator = cultureToUse.numberFormat.NumberGroupSeparator

    return parseLocalizedNumber(value, localeDecimalSeparator, localeGroupSeparator);
}
